import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Bot, TrendingUp, BookOpen, Mail, Phone, User, BarChart as ChartBar, Brain, Target, ArrowRight, Star, DollarSign, TrendingDown, Wallet } from 'lucide-react';

const supabase = import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY
  ? createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY
    )
  : null;

function App() {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    console.log('Supabase Config:', {
      url: import.meta.env.VITE_SUPABASE_URL,
      key: import.meta.env.VITE_SUPABASE_ANON_KEY?.slice(0, 8) + '...'
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setSubmitStatus('error');
      return;
    }
  
    setIsSubmitting(true);
    setSubmitStatus(null);
  
    try {
      const { error } = await supabase
        .from('registrations')
        .insert([{
          first_name: formData.name,
          last_name: formData.surname,
          email: formData.email,
          phone: formData.phone
        }])
        .select();
  
      if (error) {
        console.error('Supabase Error Details:', {
          code: error.code,
          message: error.message,
          details: error.details
        });
        throw error;
      }
      
      setSubmitStatus('success');
      setFormData({ name: '', surname: '', email: '', phone: '' });
    } catch (error) {
      console.error('Submission Error:', {
        error,
        supabaseConfig: {
          url: import.meta.env.VITE_SUPABASE_URL,
          keyPresent: !!import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
    throw new Error('Missing Supabase environment variables - check your .env file');
  }
  const testimonials = [
    {
      name: "Ahmet Yılmaz",
      role: "BIST Yatırımcısı",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80",
      quote: "6 ay önce BIST'te sürekli zarar ediyordum. AI Yatırım Akademi'nin stratejileri sayesinde portföyüm %780 büyüdü.",
      portfolio: "https://images.unsplash.com/photo-1642543492481-44e81e3914a7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
    },
    {
      name: "Zeynep Kaya",
      role: "Forex Trader",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80",
      quote: "Yapay zeka destekli analizler sayesinde forex piyasasında istikrarlı kazançlar elde ediyorum. İlk yatırımım 10 katına çıktı.",
      portfolio: "https://images.unsplash.com/photo-1640340434855-6084b1f4901c?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
    },
    {
      name: "Mehmet Demir",
      role: "Kripto Yatırımcısı",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80",
      quote: "Kripto piyasasında AI sinyalleri ile 3 ayda %1200 getiri elde ettim. Bu eğitim hayatımı değiştirdi.",
      portfolio: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&h=400&q=80"
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1639762681485-074b7f938ba0')] bg-cover bg-center opacity-5"></div>
      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <header className="text-center mb-20">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute -inset-1 bg-red-800 rounded-full blur opacity-30 animate-pulse"></div>
                <Bot className="relative w-20 h-20 text-blue-400" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-red-800">
              Yapay Zeka ile Yatırımda Zirveye
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Türkiye'nin en başarılı yatırımcıları tarafından onaylanan, yapay zeka destekli premium yatırım eğitimi.
            </p>
          </header>

          {/* Success Stories Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center text-white mb-12">
              Başarı Hikayeleri
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-lg p-6 rounded-2xl border border-blue-900/30 transform hover:scale-105 transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full border-2 border-red-800"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-white">{testimonial.name}</h3>
                      <p className="text-blue-300">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-blue-100 mb-4">{testimonial.quote}</p>
                  <div className="relative">
                    <img
                      src={testimonial.portfolio}
                      alt="Portfolio Performance"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="absolute top-2 right-2 bg-green-500/90 px-3 py-1 rounded-full text-white text-sm font-semibold">
                      +1000%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            <div className="bg-white/5 backdrop-blur-lg p-6 rounded-2xl border border-blue-900/30">
              <DollarSign className="w-10 h-10 text-green-400 mb-4" />
              <h4 className="text-4xl font-bold text-white mb-2">₺2.7M+</h4>
              <p className="text-blue-300">Ortalama Yıllık Kazanç</p>
            </div>
            <div className="bg-white/5 backdrop-blur-lg p-6 rounded-2xl border border-blue-900/30">
              <TrendingUp className="w-10 h-10 text-blue-400 mb-4" />
              <h4 className="text-4xl font-bold text-white mb-2">%92</h4>
              <p className="text-blue-300">Başarı Oranı</p>
            </div>
            <div className="bg-white/5 backdrop-blur-lg p-6 rounded-2xl border border-blue-900/30">
              <Wallet className="w-10 h-10 text-red-400 mb-4" />
              <h4 className="text-4xl font-bold text-white mb-2">5000+</h4>
              <p className="text-blue-300">Başarılı Öğrenci</p>
            </div>
            <div className="bg-white/5 backdrop-blur-lg p-6 rounded-2xl border border-blue-900/30">
              <TrendingDown className="w-10 h-10 text-green-400 mb-4" />
              <h4 className="text-4xl font-bold text-white mb-2">%0.1</h4>
              <p className="text-blue-300">Risk Oranı</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-start mb-20">
            <div className="space-y-8">
              <div className="bg-white/5 backdrop-blur-lg p-8 rounded-2xl border border-blue-900/30 transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center mb-6">
                  <ChartBar className="w-10 h-10 text-blue-400 mr-4" />
                  <h3 className="text-2xl font-bold text-white">Yapay Zeka Analizi</h3>
                </div>
                <p className="text-blue-100 leading-relaxed">
                  Özel AI algoritmalarımız, piyasadaki fırsatları %99.9 doğrulukla tespit eder ve size özel sinyaller üretir.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-lg p-8 rounded-2xl border border-blue-900/30 transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center mb-6">
                  <Brain className="w-10 h-10 text-red-800 mr-4" />
                  <h3 className="text-2xl font-bold text-white">Risk Yönetimi</h3>
                </div>
                <p className="text-blue-100 leading-relaxed">
                  Gelişmiş risk yönetimi sistemimiz ile kayıplarınızı minimize eder, kazançlarınızı maksimize ederiz.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-lg p-8 rounded-2xl border border-blue-900/30 transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center mb-6">
                  <Target className="w-10 h-10 text-blue-400 mr-4" />
                  <h3 className="text-2xl font-bold text-white">VIP Destek</h3>
                </div>
                <p className="text-blue-100 leading-relaxed">
                  7/24 uzman desteği ve özel WhatsApp grubu ile anlık piyasa analizlerine erişin.
                </p>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-lg p-8 rounded-2xl border border-blue-900/30">
              <div className="relative mb-8">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-800 to-blue-900 rounded-lg blur opacity-20"></div>
                <h2 className="relative text-3xl font-bold text-white mb-2">
                  Premium Eğitime Ücretsiz Katılın
                </h2>
                <p className="relative text-blue-200">
                  Son 50 kontenjan! Normalde ₺14,997 olan eğitim şimdi ücretsiz.
                </p>
              </div>

              {!supabase && (
                <div className="text-yellow-400 mb-6 p-4 bg-yellow-400/10 rounded-lg">
                  Bağlantı hatası: Sistem şu anda kayıtları kabul edemiyor. Lütfen daha sonra tekrar deneyin.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-blue-200 mb-1">
                      Ad
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-5 h-5" />
                      <input
                        type="text"
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="pl-10 w-full bg-white/5 border border-blue-400/30 rounded-lg py-3 px-4 text-white placeholder-blue-200/60 focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent transition-all duration-200"
                        placeholder="Adınız"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="surname" className="block text-sm font-medium text-blue-200 mb-1">
                      Soyad
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-5 h-5" />
                      <input
                        type="text"
                        id="surname"
                        required
                        value={formData.surname}
                        onChange={(e) => setFormData(prev => ({ ...prev, surname: e.target.value }))}
                        className="pl-10 w-full bg-white/5 border border-blue-400/30 rounded-lg py-3 px-4 text-white placeholder-blue-200/60 focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent transition-all duration-200"
                        placeholder="Soyadınız"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-blue-200 mb-1">
                      E-posta
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-5 h-5" />
                      <input
                        type="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10 w-full bg-white/5 border border-blue-400/30 rounded-lg py-3 px-4 text-white placeholder-blue-200/60 focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent transition-all duration-200"
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-blue-200 mb-1">
                      Telefon
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-5 h-5" />
                      <input
                        type="tel"
                        id="phone"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="pl-10 w-full bg-white/5 border border-blue-400/30 rounded-lg py-3 px-4 text-white placeholder-blue-200/60 focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent transition-all duration-200"
                        placeholder="05XX XXX XX XX"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !supabase}
                  className="w-full group bg-gradient-to-r from-red-800 to-blue-900 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 hover:from-red-900 hover:to-blue-950 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <span>{isSubmitting ? 'Kaydediliyor...' : 'ÜCRETSİZ KAYIT OL'}</span>
                  <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500/50 text-green-400 p-4 rounded-lg text-center">
                    Tebrikler! Kaydınız başarıyla alındı. VIP ekibimiz size en kısa sürede ulaşacak.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-4 rounded-lg text-center">
                    Bir hata oluştu. Lütfen tekrar deneyin.
                  </div>
                )}
              </form>
            </div>
          </div>

          <footer className="text-center text-blue-200/60">
            <p> 2024 AI Yatırım Akademi. Tüm hakları saklıdır.</p>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default App;